/* ═══════════════════════════════════════════════════════════════
   SUIT BUILDER – Bureau Bespoke
   Full interaction engine: step navigation, state management,
   conditional steps, cart integration, save-to-account
   ═══════════════════════════════════════════════════════════════ */

(function() {
  'use strict';

  /* ═══════════════════════════════════════
     CONFIGURATION
     ═══════════════════════════════════════ */
  const SCROLL_DELAY = 600;
  const BASE_PRICE = 6500;
  const PRICE_FORMAT = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  });

  const selections = {};

  const phases = {
    blazer: { start: 0, end: 14, label: 'Blazer' },
    waistcoat: { start: 15, end: 15, label: 'Waistcoat' },
    trouser: { start: 16, end: 24, label: 'Trouser' },
    measurements: { start: 25, end: 25, label: 'Fit' }
  };

  /* ═══════════════════════════════════════
     SWATCH DEFINITIONS
     ═══════════════════════════════════════ */
  const swatchDefinitions = {
    fabric: {
      colors: ['#1B2A4A', '#2A1F1A', '#3A3A3A', '#4A3728', '#111', '#5A1A1A', '#2D4A3E', '#B8976A', '#333', '#1E3A6D', '#888', '#2E1A3A'],
      names: ['Midnight Navy', 'Espresso', 'Charcoal', 'Tobacco', 'Black', 'Burgundy', 'Forest', 'Camel', 'Slate', 'Royal Blue', 'Dove Grey', 'Plum']
    },
    buttons: {
      colors: ['#3B2F1E', '#5C4A32', '#D8CFC0', '#2A2420'],
      names: ['Genuine Horn', 'Corozo Nut', 'Mother of Pearl', 'Smoked Horn']
    },
    lining: {
      colors: ['#722F37', '#1B2A4A', '#F5EDE0', '#2D4A3E', '#3A3A3A', '#1E3A6D', '#D4C5A0', '#2E1A3A', '#5A5A5A', '#4A1A1A'],
      names: ['Burgundy', 'Navy', 'Ivory', 'Forest', 'Charcoal', 'Royal Blue', 'Champagne', 'Purple', 'Slate', 'Oxblood']
    },
    undercollar: {
      colors: ['#111', '#1B2A4A', '#333', '#4A3728', '#2A3A2A', '#5A1A1A', '#B8976A', '#888', '#3A4A5A', '#2A1A0A'],
      names: ['Black', 'Navy', 'Charcoal', 'Brown', 'Forest', 'Burgundy', 'Camel', 'Grey', 'Slate', 'Espresso']
    }
  };

  /* ═══════════════════════════════════════
     UTILITY FUNCTIONS
     ═══════════════════════════════════════ */
  function scrollIntoViewSmooth(element) {
    if (!element) return;
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  function getCurrentStepIndex() {
    const chapters = document.querySelectorAll('.sb-chapter');
    let currentIndex = 0;
    let maxIntersection = 0;

    chapters.forEach((chapter, index) => {
      const rect = chapter.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = rect.height;
      const visibleHeight = Math.max(0, Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0));
      const intersection = visibleHeight / elementHeight;

      if (intersection > maxIntersection) {
        maxIntersection = intersection;
        currentIndex = index;
      }
    });

    return currentIndex;
  }

  function getPhaseForIndex(index) {
    for (const [key, phase] of Object.entries(phases)) {
      if (index >= phase.start && index <= phase.end) {
        return key;
      }
    }
    return null;
  }

  /* ═══════════════════════════════════════
     1. SCROLL REVEAL ANIMATIONS
     ═══════════════════════════════════════ */
  function initScrollReveal() {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.sbDelay || 0;
            setTimeout(() => {
              entry.target.classList.add('sb-revealed');
            }, parseInt(delay));
            revealObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px'
      }
    );

    document.querySelectorAll('[data-sb-reveal]').forEach((element) => {
      revealObserver.observe(element);
    });
  }

  /* ═══════════════════════════════════════
     2. SWATCH GRID BUILDER
     ═══════════════════════════════════════ */
  function buildSwatches(containerId, colors, names, chapterKey) {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = '';

    colors.forEach((color, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'sb-swatch-wrapper';

      const swatch = document.createElement('div');
      swatch.className = 'sb-swatch';
      swatch.style.backgroundColor = color;
      swatch.dataset.swatchIndex = index;
      swatch.dataset.swatchColor = color;
      swatch.dataset.swatchName = names[index];
      swatch.dataset.chapterKey = chapterKey;

      const name = document.createElement('span');
      name.className = 'sb-swatch-name';
      name.textContent = names[index];

      wrapper.appendChild(swatch);
      wrapper.appendChild(name);
      container.appendChild(wrapper);
    });
  }

  function initSwatchGrids() {
    buildSwatches('sb-fabric-swatches', swatchDefinitions.fabric.colors, swatchDefinitions.fabric.names, 'fabric');
    buildSwatches('sb-buttons-swatches', swatchDefinitions.buttons.colors, swatchDefinitions.buttons.names, 'buttons');
    buildSwatches('sb-lining-swatches', swatchDefinitions.lining.colors, swatchDefinitions.lining.names, 'lining');
    buildSwatches('sb-undercollar-swatches', swatchDefinitions.undercollar.colors, swatchDefinitions.undercollar.names, 'undercollar');
    buildSwatches('sb-trouser-button-swatches', swatchDefinitions.buttons.colors, swatchDefinitions.buttons.names, 'trouser-button');
    buildSwatches('sb-trouser-lining-swatches', swatchDefinitions.lining.colors, swatchDefinitions.lining.names, 'trouser-lining');
  }

  /* ═══════════════════════════════════════
     3. CARD SELECTION & AUTO-ADVANCE
     ═══════════════════════════════════════ */
  function selectCard(card) {
    const grid = card.closest('.sb-options, [data-sb-grid]');
    if (!grid) return;

    const allCards = grid.querySelectorAll('.sb-card');
    allCards.forEach((c) => c.classList.remove('sb-selected'));
    card.classList.add('sb-selected');

    const title = card.querySelector('.sb-card-title');
    const selectionValue = title ? title.textContent.trim() : '';

    const chapter = card.closest('.sb-chapter, .sb-phase-banner');
    const chapterKey = chapter ? chapter.dataset.name : '';

    if (chapterKey && selectionValue) {
      selections[chapterKey] = selectionValue;
    }

    autoAdvance();
    updateProgress();
  }

  function selectSwatch(swatch) {
    const grid = swatch.closest('[data-sb-grid], .sb-swatch-container, #sb-fabric-swatches, #sb-buttons-swatches, #sb-lining-swatches, #sb-undercollar-swatches, #sb-trouser-button-swatches, #sb-trouser-lining-swatches');
    if (!grid) return;

    const allSwatches = grid.querySelectorAll('.sb-swatch');
    allSwatches.forEach((s) => s.classList.remove('sb-selected'));
    swatch.classList.add('sb-selected');

    const chapterKey = swatch.dataset.chapterKey;
    const swatchName = swatch.dataset.swatchName;

    if (chapterKey && swatchName) {
      selections[chapterKey] = swatchName;
    }

    autoAdvance();
    updateProgress();

    if (chapterKey === 'buttons') {
      autoSyncTrouserButton(swatchName);
    } else if (chapterKey === 'lining') {
      autoSyncTrouserLining(swatchName);
    }
  }

  function autoAdvance() {
    setTimeout(() => {
      const currentStepIndex = getCurrentStepIndex();
      const chapters = document.querySelectorAll('.sb-chapter, .sb-phase-banner');

      if (currentStepIndex + 1 < chapters.length) {
        const nextSection = chapters[currentStepIndex + 1];
        scrollIntoViewSmooth(nextSection);
      }
    }, SCROLL_DELAY);
  }

  window.sbSelectCard = selectCard;

  /* ═══════════════════════════════════════
     4. PROGRESS NAV — SCROLL SPY
     ═══════════════════════════════════════ */
  function initScrollSpy() {
    const chapters = document.querySelectorAll('.sb-chapter');
    const stepItems = document.querySelectorAll('.sb-step-item');

    const scrollSpyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const chapterIndex = Array.from(chapters).indexOf(entry.target);

            stepItems.forEach((item) => item.classList.remove('sb-viewing'));
            if (stepItems[chapterIndex]) {
              stepItems[chapterIndex].classList.add('sb-viewing');
            }

            updatePhaseHeadersActive();
            updateMobileBar();
          }
        });
      },
      {
        threshold: 0.25,
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    chapters.forEach((chapter) => {
      scrollSpyObserver.observe(chapter);
    });
  }

  function updatePhaseHeadersActive() {
    const currentIndex = getCurrentStepIndex();
    const currentPhase = getPhaseForIndex(currentIndex);

    document.querySelectorAll('.sb-phase-header').forEach((header) => {
      header.classList.remove('sb-active', 'sb-completed');
      const phaseKey = header.dataset.phaseFor || header.dataset.phase;
      if (phaseKey === currentPhase) {
        header.classList.add('sb-active');
      } else {
        const phaseData = phases[phaseKey];
        if (phaseData && currentIndex > phaseData.end) {
          header.classList.add('sb-completed');
        }
      }
    });
  }

  /* ═══════════════════════════════════════
     5. PROGRESS TRACKING
     ═══════════════════════════════════════ */
  function updateProgress() {
    const chapters = document.querySelectorAll('.sb-chapter');
    const stepItems = document.querySelectorAll('.sb-step-item');

    chapters.forEach((chapter, index) => {
      const hasSelection = chapter.querySelector('.sb-selected') !== null;

      if (stepItems[index]) {
        if (hasSelection) {
          stepItems[index].classList.add('sb-completed');
        } else {
          stepItems[index].classList.remove('sb-completed');
        }
      }
    });

    updateCommission();
    updateMobileBar();
  }

  /* ═══════════════════════════════════════
     6. SCROLL TO SECTION
     ═══════════════════════════════════════ */
  window.sbScrollTo = function(index) {
    const chapters = document.querySelectorAll('.sb-chapter');
    if (chapters[index]) {
      scrollIntoViewSmooth(chapters[index]);
    }
  };

  /* ═══════════════════════════════════════
     7. MOBILE BAR UPDATES
     ═══════════════════════════════════════ */
  function updateMobileBar() {
    const currentIndex = getCurrentStepIndex();
    const chapters = document.querySelectorAll('.sb-chapter');
    const totalChapters = chapters.length;

    const fillBar = document.querySelector('.sb-mobile-fill');
    if (fillBar) {
      const percentage = ((currentIndex + 1) / totalChapters) * 100;
      fillBar.style.width = percentage + '%';
    }

    const stepNameEl = document.querySelector('.sb-mobile-step-name');
    if (stepNameEl && chapters[currentIndex]) {
      stepNameEl.textContent = chapters[currentIndex].dataset.name || '';
    }

    document.querySelectorAll('.sb-mobile-phase').forEach((tab) => {
      tab.classList.remove('sb-active', 'sb-completed');

      if (tab.dataset.phase === getPhaseForIndex(currentIndex)) {
        tab.classList.add('sb-active');
      } else {
        const phaseData = phases[tab.dataset.phase];
        if (phaseData && currentIndex > phaseData.end) {
          tab.classList.add('sb-completed');
        }
      }
    });

    buildContextualDots(getPhaseForIndex(currentIndex), currentIndex);
  }

  function buildContextualDots(currentPhase, currentIndex) {
    const dotsContainer = document.querySelector('.sb-mobile-dots');
    if (!dotsContainer) return;

    dotsContainer.innerHTML = '';

    const phaseData = phases[currentPhase];
    if (!phaseData) return;

    const chapters = document.querySelectorAll('.sb-chapter');

    for (let i = phaseData.start; i <= phaseData.end; i++) {
      if (chapters[i]) {
        const dot = document.createElement('button');
        dot.className = 'sb-mobile-dot';
        dot.textContent = (i + 1).toString();
        dot.dataset.chapterIndex = i;

        if (i === currentIndex) {
          dot.classList.add('sb-dot-active');
        } else if (i < currentIndex) {
          dot.classList.add('sb-dot-completed');
        }

        dot.addEventListener('click', () => {
          window.sbScrollTo(i);
        });

        dotsContainer.appendChild(dot);
      }
    }
  }

  /* ═══════════════════════════════════════
     8. COMMISSION / REVIEW UPDATES
     ═══════════════════════════════════════ */
  function updateCommission() {
    document.querySelectorAll('.sb-summary-row').forEach((row) => {
      const key = row.dataset.key;
      if (key && selections[key]) {
        const valueEl = row.querySelector('.sb-sum-value');
        if (valueEl) {
          valueEl.textContent = selections[key];
        }
      }
    });

    const totalEl = document.querySelector('.sb-total-price');
    if (totalEl) {
      totalEl.textContent = PRICE_FORMAT.format(BASE_PRICE);
    }
  }

  /* ═══════════════════════════════════════
     9. EDIT LINKS — SCROLL BACK
     ═══════════════════════════════════════ */
  function initEditLinks() {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('sb-sum-edit')) {
        const chapterIndex = parseInt(e.target.dataset.chapter, 10);
        if (!isNaN(chapterIndex)) {
          window.sbScrollTo(chapterIndex);
        }
      }
    });
  }

  /* ═══════════════════════════════════════
     10. WAISTCOAT CONDITIONAL
     ═══════════════════════════════════════ */
  function handleWaistcoatChange() {
    const waistcoatChapter = document.querySelector('[data-name="waistcoat"]');
    if (!waistcoatChapter) return;

    const yesCard = waistcoatChapter.querySelector('[data-value="yes"]');
    const noCard = waistcoatChapter.querySelector('[data-value="no"]');

    const conditionalSections = document.querySelectorAll('[data-sb-conditional="waistcoat"]');

    const updateConditionals = () => {
      const hasYes = waistcoatChapter.querySelector('.sb-selected[data-value="yes"]') !== null;
      conditionalSections.forEach((section) => {
        if (hasYes) {
          section.classList.add('sb-visible');
        } else {
          section.classList.remove('sb-visible');
        }
      });
    };

    if (yesCard) {
      yesCard.addEventListener('click', updateConditionals);
    }
    if (noCard) {
      noCard.addEventListener('click', updateConditionals);
    }

    updateConditionals();
  }

  /* ═══════════════════════════════════════
     11. TROUSER PRE-SELECTION SYNC
     ═══════════════════════════════════════ */
  function autoSyncTrouserButton(buttonName) {
    const trouserButtonContainer = document.getElementById('sb-trouser-button-swatches');
    if (!trouserButtonContainer) return;

    const targetSwatch = Array.from(trouserButtonContainer.querySelectorAll('.sb-swatch')).find(
      (swatch) => swatch.dataset.swatchName === buttonName
    );

    if (targetSwatch) {
      const allSwatches = trouserButtonContainer.querySelectorAll('.sb-swatch');
      allSwatches.forEach((s) => s.classList.remove('sb-selected'));
      targetSwatch.classList.add('sb-selected');
      selections['trouser-button'] = buttonName;

      const matchedLabel = targetSwatch.closest('.sb-swatch-wrapper').querySelector('.sb-matched-label');
      if (!matchedLabel) {
        const label = document.createElement('span');
        label.className = 'sb-matched-label';
        label.textContent = 'Matched from blazer';
        targetSwatch.closest('.sb-swatch-wrapper').appendChild(label);
      }
    }
  }

  function autoSyncTrouserLining(liningName) {
    const trouserLiningContainer = document.getElementById('sb-trouser-lining-swatches');
    if (!trouserLiningContainer) return;

    const targetSwatch = Array.from(trouserLiningContainer.querySelectorAll('.sb-swatch')).find(
      (swatch) => swatch.dataset.swatchName === liningName
    );

    if (targetSwatch) {
      const allSwatches = trouserLiningContainer.querySelectorAll('.sb-swatch');
      allSwatches.forEach((s) => s.classList.remove('sb-selected'));
      targetSwatch.classList.add('sb-selected');
      selections['trouser-lining'] = liningName;

      const matchedLabel = targetSwatch.closest('.sb-swatch-wrapper').querySelector('.sb-matched-label');
      if (!matchedLabel) {
        const label = document.createElement('span');
        label.className = 'sb-matched-label';
        label.textContent = 'Matched from blazer';
        targetSwatch.closest('.sb-swatch-wrapper').appendChild(label);
      }
    }
  }

  /* ═══════════════════════════════════════
     12. ADD TO CART (SHOPIFY)
     ═══════════════════════════════════════ */
  function initAddToCart() {
    const addBtn = document.querySelector('.sb-btn-accent[data-action="add-to-cart"]');
    if (!addBtn) return;

    addBtn.addEventListener('click', async (e) => {
      e.preventDefault();

      const variantId = addBtn.dataset.variantId;
      if (!variantId) {
        console.error('Variant ID not found');
        return;
      }

      const cartData = {
        id: variantId,
        quantity: 1,
        properties: { ...selections }
      };

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(cartData)
        });

        if (response.ok) {
          window.location.href = '/cart';
        } else {
          showError('Failed to add to cart. Please try again.');
        }
      } catch (error) {
        console.error('Cart error:', error);
        showError('An error occurred. Please try again.');
      }
    });
  }

  function showError(message) {
    const errorEl = document.querySelector('.sb-error-message');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add('sb-visible');
      setTimeout(() => {
        errorEl.classList.remove('sb-visible');
      }, 4000);
    }
  }

  /* ═══════════════════════════════════════
     13. SAVE BUILD
     ═══════════════════════════════════════ */
  function initSaveBuild() {
    const saveBtn = document.querySelector('.sb-btn-outline[data-action="save-build"]');
    if (!saveBtn) return;

    saveBtn.addEventListener('click', (e) => {
      e.preventDefault();

      const buildName = prompt('Name your custom suit build:', 'My Suit Build');
      if (!buildName) return;

      const buildData = {
        name: buildName,
        timestamp: new Date().toISOString(),
        selections: { ...selections }
      };

      const builds = JSON.parse(localStorage.getItem('sb-builds') || '[]');
      builds.push(buildData);
      localStorage.setItem('sb-builds', JSON.stringify(builds));

      showSaveSuccess('Build saved successfully!');
    });
  }

  function showSaveSuccess(message) {
    const successEl = document.querySelector('.sb-success-message');
    if (successEl) {
      successEl.textContent = message;
      successEl.classList.add('sb-visible');
      setTimeout(() => {
        successEl.classList.remove('sb-visible');
      }, 3000);
    }
  }

  /* ═══════════════════════════════════════
     14. KEYBOARD NAVIGATION
     ═══════════════════════════════════════ */
  function initKeyboardNav() {
    document.addEventListener('keydown', (e) => {
      const currentIndex = getCurrentStepIndex();
      const chapters = document.querySelectorAll('.sb-chapter');

      if (e.key === 'ArrowDown' && currentIndex + 1 < chapters.length) {
        e.preventDefault();
        window.sbScrollTo(currentIndex + 1);
      } else if (e.key === 'ArrowUp' && currentIndex > 0) {
        e.preventDefault();
        window.sbScrollTo(currentIndex - 1);
      }
    });
  }

  /* ═══════════════════════════════════════
     EVENT DELEGATION FOR CARDS & SWATCHES
     ═══════════════════════════════════════ */
  function initEventDelegation() {
    document.addEventListener('click', (e) => {
      const card = e.target.closest('.sb-card');
      if (card) {
        selectCard(card);
      }

      const swatch = e.target.closest('.sb-swatch');
      if (swatch && !card) {
        selectSwatch(swatch);
      }
    });
  }

  /* ═══════════════════════════════════════
     INITIALIZATION
     ═══════════════════════════════════════ */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    initScrollReveal();
    initSwatchGrids();
    initScrollSpy();
    initEventDelegation();
    initEditLinks();
    initAddToCart();
    initSaveBuild();
    initKeyboardNav();
    handleWaistcoatChange();

    updateProgress();
    updateMobileBar();
    updatePhaseHeadersActive();
    updateCommission();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
